import MappingTopBox from "../CreatorTools/MappingTool/components/MappingTopBox";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import type { CustomContext } from "@rebuild/shared";
import { useOrganizationSelect } from "../../hooks/useOrganizationSelect";
import OrganizationSelector from "../../components/ui/utils/OrganizationSelector";
import EstimateTypeSelector from "../../components/ui/utils/EstimateTypeSelector";
import { EstimateType } from "../CreatorTools/MappingTool/utils/EstimateType";
import { deleteCustomContext, fetchCustomContexts, saveCustomContext } from "./customContextAPI";
import type { DraftEntry } from "./customContext.types";
import CustomContextTable from "./CustomContextTable";
import EditModal from "./EditModal";
import ConfirmDeleteModal from "./ConfirmDeleteModal";

function CustomContextPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <MappingTopBox />
      <div className="ml-8 mr-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2 mt-4">Custom Context</h1>
        <p className="text-gray-600 mb-2">ENGINEERS ONLY!!! (For now)</p>
        <p className="text-gray-600 mb-6">
          Extra context injected into the line item processing prompts. Each record targets a list of organizations and estimate types.{" "}
        </p>
        <CustomContextComponent />
      </div>
    </div>
  );
}

function CustomContextComponent() {
  const [entries, setEntries] = useState<CustomContext[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [draft, setDraft] = useState<DraftEntry | undefined>();
  const [entryToDelete, setEntryToDelete] = useState<CustomContext | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const [orgFilterId, setOrgFilterId] = useState<string | undefined>();
  const [estimateTypeFilter, setEstimateTypeFilter] = useState<EstimateType | undefined>();

  const { organizations } = useOrganizationSelect({ includeTestOrgs: true });

  const orgNameById = useMemo(() => {
    const map = new Map<string, string>();
    organizations.forEach((org) => map.set(org.organizationId, org.organizationName ?? org.organizationId));
    return map;
  }, [organizations]);

  const orgFilter = useMemo(() => organizations.find((org) => org.organizationId === orgFilterId), [organizations, orgFilterId]);

  const filteredEntries = useMemo(
    () =>
      entries.filter(
        (entry) =>
          (!orgFilterId || entry.organizationIds.includes(orgFilterId)) &&
          (!estimateTypeFilter || (entry.estimateTypes as string[]).includes(estimateTypeFilter)),
      ),
    [entries, orgFilterId, estimateTypeFilter],
  );

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      setEntries(await fetchCustomContexts());
    } catch (e) {
      toast.error("Failed to load custom context");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onSave = async () => {
    setIsSaving(true);
    try {
      await saveCustomContext(draft);
      await refresh();
      setDraft(undefined);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsSaving(false);
    }
  };

  const onDelete = async () => {
    if (!entryToDelete) return;
    try {
      await deleteCustomContext(entryToDelete.id);
      await refresh();
    } catch (e) {
      toast.error("Failed to delete custom context");
    } finally {
      setEntryToDelete(undefined);
    }
  };

  const onAddNewCustomContext = useCallback(() => setDraft({ title: "", organizationIds: [], estimateTypes: [], context: "" }), []);

  const { isSaveDisabled, saveDisabledReason } = useMemo(() => {
    if (!draft) return { isSaveDisabled: true, saveDisabledReason: undefined };
    if (!draft.title.trim()) return { isSaveDisabled: true, saveDisabledReason: "Please add a title." };
    if (draft.organizationIds.length === 0) return { isSaveDisabled: true, saveDisabledReason: "Please select at least one organization." };
    if (draft.estimateTypes.length === 0) return { isSaveDisabled: true, saveDisabledReason: "Please select at least one estimate type." };
    if (!draft.context.trim()) return { isSaveDisabled: true, saveDisabledReason: "Please add custom context." };
    return { isSaveDisabled: false, saveDisabledReason: undefined };
  }, [draft]);

  return (
    <>
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-2">
          {entries.length > 0 && (
            <div className="flex flex-wrap items-end gap-4 mb-4">
              <div className="min-w-[240px]">
                <OrganizationSelector
                  label="Filter by Organization"
                  organizations={organizations}
                  selectedOrganization={orgFilter}
                  onSelect={(org) => setOrgFilterId(org?.organizationId)}
                />
              </div>
              <div className="min-w-[240px]">
                <EstimateTypeSelector
                  label="Filter by Estimate Type"
                  placeholder="All estimate types"
                  allowEmpty
                  value={estimateTypeFilter}
                  onChange={setEstimateTypeFilter}
                />
              </div>
            </div>
          )}
          <button
            disabled={isLoading}
            onClick={onAddNewCustomContext}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:hover:bg-gray-400 transition-colors"
          >
            Add Custom Context
          </button>
        </div>

        {isLoading && entries.length === 0 ? (
          <p className="text-gray-500">Loading...</p>
        ) : entries.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-gray-500">No custom context added yet.</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-gray-500">No custom context matches the selected filters.</p>
          </div>
        ) : (
          <CustomContextTable
            entries={filteredEntries}
            orgNameById={orgNameById}
            onSetDraft={setDraft}
            onSetEntryToDelete={setEntryToDelete}
          />
        )}
      </div>

      {draft && (
        <EditModal
          title={draft.id ? "Edit Custom Context" : "Add Custom Context"}
          draft={draft}
          organizations={organizations}
          orgNameById={orgNameById}
          onChange={setDraft}
          onClose={() => setDraft(undefined)}
          onSave={onSave}
          isSaving={isSaving}
          isDisabled={isSaveDisabled}
          disabledReason={saveDisabledReason}
        />
      )}

      {entryToDelete && <ConfirmDeleteModal entry={entryToDelete} onClose={() => setEntryToDelete(undefined)} onConfirm={onDelete} />}
    </>
  );
}

export default CustomContextPage;
